
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DevelopmentRecipe, DevelopmentSession } from '@/types/develop';
import { Button } from '@/components/ui/button';
import { Trash, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import { useTranslation } from '@/hooks/useTranslation';

interface DevelopmentHistoryProps {
  sessions: DevelopmentSession[];
  recipes: DevelopmentRecipe[];
  onDelete: (id: string) => void;
  darkMode?: boolean;
}

export const DevelopmentHistory: React.FC<DevelopmentHistoryProps> = ({
  sessions,
  recipes,
  onDelete,
  darkMode = false
}) => {
  const { t } = useTranslation();
  
  // Sort sessions by date, most recent first
  const sortedSessions = [...sessions].sort((a, b) => b.startTime - a.startTime);
  
  // Function to find recipe by ID
  const findRecipe = (recipeId: string) => {
    return recipes.find(recipe => recipe.id === recipeId);
  };
  
  // Function to format duration in a readable way
  const formatDuration = (startMs: number, endMs?: number) => {
    if (!endMs) return '(In progress)';
    
    const durationMs = endMs - startMs;
    const minutes = Math.floor(durationMs / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    
    return `${minutes}m ${seconds}s`;
  };
  
  return (
    <Card className={cn(
      'w-full',
      darkMode && 'bg-black border-red-900'
    )}>
      <CardHeader>
        <CardTitle className={cn(
          'text-lg font-semibold',
          darkMode && 'text-red-400'
        )}>
          Development History
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {sortedSessions.length === 0 ? (
          <div className={cn(
            'text-muted-foreground text-center py-6',
            darkMode && 'text-red-700'
          )}>
            No development sessions yet. Start using recipes to track your film development history.
          </div>
        ) : (
          <ul className={cn(
            'divide-y',
            darkMode && 'divide-red-900'
          )}>
            {sortedSessions.map(session => {
              const recipe = findRecipe(session.recipeId);
              
              return (
                <li key={session.id} className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        {session.success ? (
                          <CheckCircle className={cn(
                            'h-4 w-4 mr-1',
                            darkMode ? 'text-red-500' : 'text-green-500'
                          )} />
                        ) : (
                          <AlertCircle className={cn(
                            'h-4 w-4 mr-1', 
                            darkMode ? 'text-yellow-600' : 'text-yellow-500'
                          )} />
                        )}
                        
                        <h3 className={cn(
                          'font-medium',
                          darkMode && 'text-red-400'
                        )}>
                          {recipe?.name || 'Unknown Recipe'}
                        </h3>
                      </div>
                      
                      <div className={cn(
                        'text-sm text-muted-foreground',
                        darkMode && 'text-red-700'
                      )}>
                        {format(new Date(session.startTime), 'PPP')} ({formatDistanceToNow(new Date(session.startTime), { addSuffix: true })})
                      </div>
                      
                      <div className={cn(
                        'text-sm',
                        darkMode && 'text-red-500'
                      )}>
                        Duration: {formatDuration(session.startTime, session.endTime)}
                      </div>
                      
                      {session.notes && (
                        <div className={cn(
                          'mt-2 text-sm p-2 bg-muted rounded-md',
                          darkMode && 'bg-red-950 text-red-400'
                        )}>
                          {session.notes}
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => onDelete(session.id)}
                      className={darkMode ? 'text-red-500 hover:bg-red-950 hover:text-red-300' : ''}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
